import React, { useCallback, useMemo } from 'react';
import {
  useGridApiRef,
  GridRowSelectionModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import styled from 'styled-components';
import { AdvancedFilterType, Person, Row } from 'talent_matching/types';
import HeaderRow from 'talent_matching/components/header_row';
import FilterRow from 'talent_matching/components/filters/filter_row';
import CandidateSidePanel from 'talent_matching/components/side_panel/candidate_side_panel';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { useCalibrationModal } from 'talent_matching/contexts/calibration_modal_context';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import { RowDef } from '@grnhse/seedling/lib/birch/components/table/types';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import RankingLoader from 'talent_matching/components/table/ranking_loader';
import NoResultsOverlay from 'talent_matching/components/no_results_overlay';
import useQueryHelper from 'talent_matching/hooks/use_query_helper';
import { useColumns } from 'talent_matching/components/table/columns';
import BaseDataTable from 'talent_matching/components/shared/base_data_table';
import DataViewTableFooter from 'talent_matching/components/table/data_view_table_footer';
import { useColumnState } from 'talent_matching/hooks/use_column_state';
import useStickyGridHeader from 'talent_matching/hooks/use_sticky_grid_header';
import { RejectModalProvider } from '../../../application_review/components/reject_modal_context';
import { NON_TOGGLABLE_COLUMNS } from 'talent_matching/hooks/use_column_visibility';
import { AdvancedCandidateCountProvider } from 'talent_matching/contexts/advanced_candidate_count_context';
import { RowActionsModalProvider } from 'talent_matching/contexts/row_actions_modal_context';
import useSidePanel from 'talent_matching/hooks/use_side_panel';
import useQuestionColumns from 'talent_matching/hooks/use_question_columns';
import SyncedRow from 'talent_matching/components/table/synced_row';
import { FORM_DEFAULTS } from 'talent_matching/components/filter_form';
import {
  extractFormFieldsFromSavedSearch,
  toSavedSearchQueryString,
} from 'talent_matching/components/filters/utils';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import type { SavedSearch } from 'talent_search/shared/types';
import SavedFilters from 'talent_matching/components/filters/saved_filters';
import { useSavedFilters } from 'talent_matching/hooks/use_saved_filters';

type Props = {
  rows: Row[];
  totalRows: number;
  loading: boolean;
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRowSelected: (selectedRows: GridRowSelectionModel) => void;
  selectionsHelper: SelectionsHelper;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  defaultColumnVisibilityModel?: GridColumnVisibilityModel;
  preSelectedRow: Row | null;
  setPreSelectedRow: (row: Row | null) => void;
};

export const MinHeightStack = styled(Stack)`
  min-height: 750px;
`;

const Wrapper = styled.div`
  .highlighted-row {
    background-color: ${FullPalette.GREY_E500};
  }
`;

const DataViewTable = ({
  rows,
  totalRows,
  loading,
  rowsPerPage,
  currentPage,
  onPageChange,
  onRowSelected,
  selectionsHelper,
  columnVisibilityModel,
  setColumnVisibilityModel,
  defaultColumnVisibilityModel,
  preSelectedRow,
  setPreSelectedRow,
}: Props) => {
  const {
    canBulkEdit,
    savedSearchOptions,
    setSavedSearchOptions,
    savedFilterSet,
    setSavedFilterSet,
  } = useTalentMatchingTabContext();
  const { getValues } = useFormContext();

  const { openCalibrationModal, showCalibrationModal } = useCalibrationModal();

  const buildAppReviewQueryString = useCallback((): string => {
    const formValues = getValues();
    formValues.advancedFilters = formValues.advancedFilters.filter(
      (filter: AdvancedFilterType) => new AdvancedFilter(filter).applied()
    );
    formValues.educationFilters = (formValues.educationFilters || []).filter(
      (filter: AdvancedFilterType) => new AdvancedFilter(filter).applied()
    );
    return toSavedSearchQueryString(formValues);
  }, [getValues]);

  const { savedFiltersProps, handleClearAll } = useSavedFilters({
    featureType: 'talent_matching',
    formDefaults: FORM_DEFAULTS as Record<string, unknown>,
    savedFilterSet,
    setSavedFilterSet,
    savedSearchOptions,
    setSavedSearchOptions,
    parseSavedSearch: (response) =>
      extractFormFieldsFromSavedSearch(
        response as SavedSearch
      ) as unknown as Record<string, unknown>,
    buildQueryString: buildAppReviewQueryString,
  });

  const apiRef = useGridApiRef();

  const { questionColumns, columnVisibilityModelWithQuestionDefaults } =
    useQuestionColumns({
      rows,
      columnVisibilityModel,
      setColumnVisibilityModel,
    });

  const { handleColumnChange } = useColumnState(
    apiRef,
    columnVisibilityModelWithQuestionDefaults
  );

  const { excludePeople, invalidatePeople } = useQueryHelper();

  const {
    isPanelOpen,
    setIsPanelOpen,
    selectedRow,
    selectedRowIndex,
    popoverRef,
    previousRow,
    nextRow,
    candidatePrefetchBatch,
    handlePanelToggle,
    handleRowClick,
    getRowClassName,
    navigateAfterRemoval,
  } = useSidePanel({ rows, preSelectedRow, setPreSelectedRow });

  const { rankingInProgress, isBackgroundRanking } = useRankingInProgress();
  const userInitiatedRankingInProgress =
    rankingInProgress && !isBackgroundRanking;
  const { wrapperRef } = useStickyGridHeader({
    disabled: userInitiatedRankingInProgress,
  });

  const onMergeSuccess = () => {
    if (isPanelOpen && selectedRow?.id === selectedRow?.id) {
      navigateAfterRemoval();
    }
    invalidatePeople();
  };

  const onRejectSuccess = (row: Row, closeModal: () => void) => {
    closeModal();

    if (isPanelOpen && row.id === selectedRow?.id) {
      navigateAfterRemoval();
    }

    excludePeople([row.id]);
  };

  const columns = useColumns({
    selectionsHelper,
    isPanelOpen,
    selectedRow,
    openCalibrationModal,
    onRejectSuccess,
    questionColumns,
  });

  const columnSelectorColumns = useMemo(
    () =>
      columns
        .filter((c) => !NON_TOGGLABLE_COLUMNS.includes(c.field))
        .map((c) => ({
          field: c.field,
          label: c.headerName || c.field,
        })),
    [columns]
  );

  return (
    <AdvancedCandidateCountProvider openCalibrationModal={openCalibrationModal}>
      <RowActionsModalProvider onRejectSuccess={onRejectSuccess}>
        <FullWidth>
          <MinHeightStack spacing={2}>
            <HeaderRow
              selectionsHelper={selectionsHelper}
              apiRef={apiRef}
              columnSelectorColumns={columnSelectorColumns}
              columnVisibilityModel={columnVisibilityModelWithQuestionDefaults}
              setColumnVisibilityModel={setColumnVisibilityModel}
              defaultColumnVisibilityModel={defaultColumnVisibilityModel}
            />
            {canBulkEdit && (
              <FilterRow onClearAll={handleClearAll}>
                <SavedFilters {...savedFiltersProps} />
              </FilterRow>
            )}
            {userInitiatedRankingInProgress ? (
              <RankingLoader />
            ) : (
              <Wrapper ref={wrapperRef}>
                <BaseDataTable
                  rows={rows}
                  columns={columns}
                  totalRows={totalRows}
                  loading={loading}
                  rowsPerPage={rowsPerPage}
                  currentPage={currentPage}
                  onPageChange={onPageChange}
                  onRowSelected={onRowSelected}
                  apiRef={apiRef}
                  columnVisibilityModel={
                    columnVisibilityModelWithQuestionDefaults
                  }
                  onColumnVisibilityModelChange={setColumnVisibilityModel}
                  onColumnWidthChange={handleColumnChange}
                  onRowClick={(row: RowDef) => handleRowClick(row as Person)}
                  getRowClassName={getRowClassName}
                  footer={() => (
                    <DataViewTableFooter rowsPerPage={rowsPerPage} />
                  )}
                  slots={{
                    noRowsOverlay: NoResultsOverlay,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    row: SyncedRow as React.ComponentType<any>,
                  }}
                />
              </Wrapper>
            )}
          </MinHeightStack>

          {selectedRow && (
            <RejectModalProvider>
              <CandidateSidePanel
                ref={popoverRef}
                onClose={() => setIsPanelOpen(false)}
                onToggle={handlePanelToggle}
                applicationId={selectedRow.id}
                isVisible={isPanelOpen}
                currentIndex={selectedRowIndex}
                rowCount={rows.length}
                previousCandidate={previousRow}
                nextCandidate={nextRow}
                candidatePrefetchBatch={candidatePrefetchBatch}
                selectCandidate={handleRowClick}
                openCalibrationModal={openCalibrationModal}
                calibrationModalOpen={showCalibrationModal}
                onRejectSuccess={onRejectSuccess}
                onMergeSuccess={onMergeSuccess}
                onAdvanceSuccess={() => {
                  if (isPanelOpen) {
                    navigateAfterRemoval();
                  }
                }}
              />
            </RejectModalProvider>
          )}
        </FullWidth>
      </RowActionsModalProvider>
    </AdvancedCandidateCountProvider>
  );
};

export default DataViewTable;
